import type { SupportedLanguage } from "../../interfaces";

/**
 * THE "date | location" line under a congress name, for every surface that
 * draws one: the room Discovery hero, the public Discovery hero and the
 * Resources section header. Two variants existed as near-identical copies until
 * they were merged here; the differences were exactly `monthStyle` and
 * `placeSeparator`, so both are parameters rather than a second function.
 *
 * `contentLang` is the CONTENT language, NOT the interface locale. A congress
 * date sits in the same line as its city and country, which
 * `resolveCongressNames` already resolved on the content axis, so formatting the
 * date on the interface axis produced a line in two languages
 * ("5-9 septembre 2026 | Athens, Greece"). See the axis rule in
 * `domains/app/hooks/useActiveLang.ts`.
 *
 * Dates are formatted in UTC: the congress days are calendar days as the
 * organiser published them, not instants to be shifted into the viewer's zone.
 */
type CongressDateFields = {
  startDate: string | null;
  endDate: string | null;
  city: string | null;
  country: string | null;
};

/**
 * Every parameter is required. Neither the month width nor the separator has a
 * defensible default — they are per-surface design choices, and defaulting one
 * would let a new surface silently inherit another surface's look.
 */
type CongressDateLabelStyle = {
  contentLang: SupportedLanguage;
  /** `long` on the hero banners ("September"), `short` in Resources ("Sept") */
  monthStyle: "long" | "short";
  /** `" | "` on the hero banners, `" - "` in Resources, `" · "` on desktop */
  placeSeparator: " | " | " - " | " · ";
};

function formatDateRange(
  startIso: string | null,
  endIso: string | null,
  contentLang: SupportedLanguage,
  monthStyle: "long" | "short"
): string {
  if (startIso === null || endIso === null) return "";
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameMonth =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth();
  if (sameMonth) {
    const monthYear = start.toLocaleDateString(contentLang, {
      month: monthStyle,
      year: "numeric",
      timeZone: "UTC",
    });
    return `${start.getUTCDate()}-${end.getUTCDate()} ${monthYear}`;
  }
  const opts = {
    day: "numeric",
    month: monthStyle,
    year: "numeric",
    timeZone: "UTC",
  } as const;
  return `${start.toLocaleDateString(contentLang, opts)} - ${end.toLocaleDateString(contentLang, opts)}`;
}

export function buildCongressDateLabel(
  fields: CongressDateFields,
  style: CongressDateLabelStyle
): string {
  const datePart = formatDateRange(
    fields.startDate,
    fields.endDate,
    style.contentLang,
    style.monthStyle
  );
  const location = [fields.city, fields.country]
    .filter((part) => part !== null && part !== "")
    .join(", ");
  if (datePart === "") return location;
  if (location === "") return datePart;
  return `${datePart}${style.placeSeparator}${location}`;
}

/**
 * One congress day as the desktop surfaces print it ("Sat, 5 Sep"), in the
 * content language and in UTC, the same calendar as the range above.
 */
export function formatCongressDay(
  date: Date,
  contentLang: SupportedLanguage
): string {
  return date.toLocaleDateString(contentLang, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
